/** @format */

import {
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
} from "@mui/material";
import Iconify from "./Iconify";
import { LoadingButton } from "@mui/lab";
import { useState } from "react";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";
import BootstrapDialogTitle from "./BootstrapDialogTitle";
import swal from "../reusables/CustomSwal";
import { Form, FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import User from "../dao/User";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    minWidth: 500,
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(2),
  },
}));

ChangePasswordForm.propTypes = {
  id: PropTypes.number,
  open: PropTypes.bool,
  setOpen: PropTypes.func,
};

export default function ChangePasswordForm({ id, open, setOpen }) {
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const onFormSubmit = async (values) => {
    // console.log(values);
    let popupResult = await swal.fire({
      title: "Confirm",
      text: "Change password?",
      icon: "question",
      showConfirmButton: true,
      showCancelButton: true,
      confirmButtonText: "Save",
    });
    if (popupResult.isConfirmed) {
      try {
        let body = {
          password: values.currentPassword,
          new_password: values.newPassword,
        };
        await User.updateSelfPassword(body);
        await swal.fire({
          title: "Success",
          text: "Your password is saved.",
          icon: "success",
          confirmButtonText: "Back",
        });
        setOpen(false);
      } catch (e) {
        let errorMessage = "An error has occurred. Please try again later.";
        if (e.code === "PASSWORD_WRONG")
          errorMessage = "Your current password is incorrect.";
        await swal.fire({
          title: "Error",
          text: errorMessage,
          icon: "error",
        });
      }
    }
  };

  const PasswordSchema = Yup.object().shape({
    currentPassword: Yup.string().required("Password is required."),
    newPassword: Yup.string().required("Password is required."),
    confirmPassword: Yup.string()
      .required("Please re-enter the password.")
      .oneOf([Yup.ref("newPassword"), null], "Password does not match."),
  });
  const formik = useFormik({
    initialValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: PasswordSchema,
    onSubmit: onFormSubmit,
  });
  const { errors, touched, handleSubmit, isSubmitting, getFieldProps } = formik;

  return (
    <BootstrapDialog open={open} onClose={() => setOpen(false)}>
      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <BootstrapDialogTitle onClose={() => setOpen(false)}>
            Change Password
          </BootstrapDialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 2, mx: 2 }}>
              <TextField
                fullWidth
                type={showCurrentPassword ? "text" : "password"}
                label="Current Password*"
                autoComplete="off"
                {...getFieldProps("currentPassword")}
                error={Boolean(
                  touched.currentPassword && errors.currentPassword
                )}
                helperText={touched.currentPassword && errors.currentPassword}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        onClick={() => setShowCurrentPassword((prev) => !prev)}
                      >
                        <Iconify
                          icon={
                            showCurrentPassword
                              ? "eva:eye-outline"
                              : "eva:eye-off-outline"
                          }
                        />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                fullWidth
                type={showNewPassword ? "text" : "password"}
                label="New Password*"
                autoComplete="off"
                {...getFieldProps("newPassword")}
                error={Boolean(touched.newPassword && errors.newPassword)}
                helperText={touched.newPassword && errors.newPassword}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        onClick={() => setShowNewPassword((prev) => !prev)}
                      >
                        <Iconify
                          icon={
                            showNewPassword
                              ? "eva:eye-outline"
                              : "eva:eye-off-outline"
                          }
                        />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                fullWidth
                type={showConfirmPassword ? "text" : "password"}
                label="Confirm Password*"
                autoComplete="new-password"
                {...getFieldProps("confirmPassword")}
                error={Boolean(
                  touched.confirmPassword && errors.confirmPassword
                )}
                helperText={touched.confirmPassword && errors.confirmPassword}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                      >
                        <Iconify
                          icon={
                            showConfirmPassword
                              ? "eva:eye-outline"
                              : "eva:eye-off-outline"
                          }
                        />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <LoadingButton
              size="large"
              type="submit"
              variant="contained"
              loading={isSubmitting}
            >
              <Iconify
                icon="eva:save-outline"
                width={22}
                height={22}
                sx={{ marginRight: 1 }}
              />
              Submit
            </LoadingButton>
          </DialogActions>
        </Form>
      </FormikProvider>
    </BootstrapDialog>
  );
}
